import React, { useEffect, useMemo, useState } from "react";
import { downloadCsv, toCsv } from "shared/utils/csv";
import { formatDate } from "shared/utils/formatDate";
import { Entitlement, getPaymentEntitlements } from "../api/orders.api";
import "../../../styles/legacy/OrderHistory.css";

export type Order = {
  title: string;
  date: string;
  status: string;
  type: string;
  price: string;
  transaction_id: string;
  live?: boolean;
};

export type OrderStatusFilter = "All" | "active" | "expired";

const STATUS_FILTERS: OrderStatusFilter[] = ["All", "active", "expired"];

const DEMO_ORDERS: Order[] = [
  {
    title: "Metatopia in 3D",
    date: "Sep 22, 2025",
    status: "active",
    type: "Buy",
    price: "€11.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Western Front: A soldier's point of view",
    date: "Mar 15, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Children of the void",
    date: "Mar 13, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Children of the void",
    date: "Mar 13, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Children of the void",
    date: "Mar 13, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Children of the void",
    date: "Mar 13, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Children of the void",
    date: "Mar 13, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
  {
    title: "Children of the void",
    date: "Mar 13, 2025",
    status: "expired",
    type: "Rent",
    price: "€3.99",
    transaction_id: "ch_1NtrTV2eZvKYlo2CjT2WUvqT",
  },
];

export function entitlementToOrder(entitlement: Entitlement): Order {
  return {
    title: entitlement.title,
    date: formatDate(entitlement.date) || entitlement.date || "—",
    status: entitlement.status,
    type: entitlement.type,
    price: entitlement.price,
    transaction_id: entitlement.transactionId || entitlement.id,
    live: true,
  };
}

export function filterOrders(
  orders: Order[],
  statusFilter: OrderStatusFilter,
  search: string
): Order[] {
  const q = search.trim().toLowerCase();
  return orders.filter((order) => {
    const matchesStatus = statusFilter === "All" || order.status === statusFilter;
    const matchesSearch =
      !q ||
      order.title.toLowerCase().includes(q) ||
      order.type.toLowerCase().includes(q) ||
      order.transaction_id.toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });
}

const OrderHistoryPage: React.FC = () => {
  const [liveOrders, setLiveOrders] = useState<Order[]>([]);
  const [storeNotice, setStoreNotice] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;
    getPaymentEntitlements()
      .then((response) => {
        if (!active) return;
        setLiveOrders(response.entitlements.map(entitlementToOrder));
        if (response.storeUnavailable) {
          setStoreNotice("The store is temporarily unavailable; showing cached entitlements.");
        }
      })
      .catch(() => {
        // Entitlements are an enhancement; the page still shows historic orders.
      });
    return () => {
      active = false;
    };
  }, []);

  const orders = useMemo(() => [...liveOrders, ...DEMO_ORDERS], [liveOrders]);
  const filteredOrders = useMemo(
    () => filterOrders(orders, statusFilter, search),
    [orders, statusFilter, search]
  );

  const downloadCSV = () => {
    const csv = toCsv(
      ["title", "date", "status", "type", "price", "transaction_id"],
      filteredOrders.map((order) => [
        order.title,
        order.date,
        order.status,
        order.type,
        order.price,
        order.transaction_id,
      ])
    );
    downloadCsv(csv, "orders.csv");
  };

  return orders.length > 0 ? (
    <div className="library-order-content">
      <div className="main-order-history">
        <div className="top-container-order-history">
          <div className="left-top-container-order-history">
            <div className="title-top-container-order-history">
              Order history
            </div>
            <div className="subtitle-top-container-order-history">
              View your orders and live entitlements
            </div>
          </div>
          <div className="right-top-container-order-history">
            <div className="order-history-toolbar">
              <input
                type="search"
                className="order-history-search"
                placeholder="Search titles or transactions…"
                aria-label="Search orders"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="order-history-filter"
                aria-label="Filter orders by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as OrderStatusFilter)}
              >
                {STATUS_FILTERS.map((value) => (
                  <option key={value} value={value}>
                    {value === "All" ? "All statuses" : value}
                  </option>
                ))}
              </select>
              <button
                className="order-history-top-container-button"
                onClick={downloadCSV}
                disabled={filteredOrders.length === 0}
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
        {storeNotice && (
          <div className="order-history-notice" role="status">
            {storeNotice}
          </div>
        )}
        <div className="table-wrapper-order-history">
          <table className="order-history-table">
            <thead>
              <tr>
                <th className="th-order-history">Title</th>
                <th className="th-order-history">Date</th>
                <th className="th-order-history">Status</th>
                <th className="th-order-history">Type</th>
                <th className="th-order-history">Price</th>
                <th className="th-order-history" id="bigger-td-order-history">
                  Transaction ID
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr className="order-row">
                  <td className="td-order-history order-history-empty" colSpan={6}>
                    No orders match the current filters.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order, idx) => (
                  <tr
                    key={order.transaction_id + idx}
                    className={order.live ? "order-row order-row--live" : "order-row"}
                    data-testid={order.live ? "live-entitlement-row" : "order-row"}
                  >
                    <td className="td-order-history" id="bigger-td-order-history">
                      {order.title}
                      {order.live && <span className="order-live-badge">Live</span>}
                    </td>
                    <td className="td-order-history">{order.date}</td>
                    <td className="td-order-history">
                      <span
                        className={
                          order.status === "expired"
                            ? "expired-order-history"
                            : order.status === "active"
                            ? "active-order-history"
                            : ""
                        }
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="td-order-history">{order.type}</td>
                    <td className="td-order-history">{order.price}</td>
                    <td className="td-order-history" id="bigger-td-order-history">
                      {order.transaction_id}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  ) : (
    <div className="library-no-order-content">
      <div className="library-order-frame">
        <h1 className="no-orders">No orders yet</h1>
        <p className="purchase-content">
          Any bought or rented content will appear here.
        </p>
      </div>
    </div>
  );
};

export default OrderHistoryPage;
